import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Link,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useUserType } from '../../contexts/UserTypeContext';
import { getUserProfile } from '../../services/supabaseService';
import { getSNSConnections, connectSNS, disconnectSNS } from '../../services/supabaseService';
import type { UserProfile } from '../../services/supabaseService';
import { Navigate } from 'react-router-dom';
import Header from '../../components/Header';
import { ProfileSection } from '../../components/mypage/ProfileSection';
import { NotificationsOutlined, ChevronRight, Menu } from '@mui/icons-material';
import { SNSConnection, SNSType } from '../../types/sns';
import { 
  BlogIcon,
  InstagramIcon,
  YouTubeIcon,
  NaverIcon,
  GoogleIcon
} from '../../components/icons/SNSIcons';
import ReviewerDrawer from '../../components/drawers/ReviewerDrawer';

const SNS_TYPES: { type: SNSType; label: string; placeholder: string; icon: React.FC<any> }[] = [
  { type: 'blog', label: '네이버블로그', placeholder: 'https://blog.naver.com/블로그ID', icon: BlogIcon },
  { type: 'instagram', label: '인스타그램', placeholder: '인스타그램 ID', icon: InstagramIcon },
  { type: 'youtube', label: '유튜브', placeholder: '유튜브 채널 URL', icon: YouTubeIcon },
  { type: 'naver', label: '네이버', placeholder: '네이버 ID', icon: NaverIcon },
];

const ReviewerMyPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [snsConnections, setSnsConnections] = useState<SNSConnection[]>([]);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [selectedSNSType, setSelectedSNSType] = useState<SNSType | null>(null);
  const [snsInput, setSnsInput] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (currentUser?.uid) {
          const [userProfile, connections] = await Promise.all([
            getUserProfile(currentUser.uid),
            getSNSConnections(currentUser.uid)
          ]);
          setProfile(userProfile);
          setSnsConnections(connections);
        }
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser]);

  const handleConnectClick = (type: SNSType) => {
    setSelectedSNSType(type);
    setSnsInput('');
    setConnectDialogOpen(true);
  };

  const handleConnect = async () => {
    try {
      if (!currentUser?.uid || !selectedSNSType || !snsInput) return;

      const connectionData = {
        type: selectedSNSType,
        url: snsInput.startsWith('http') ? snsInput : undefined,
        username: !snsInput.startsWith('http') ? snsInput : undefined
      };

      const newConnection = await connectSNS(currentUser.uid, connectionData);
      setSnsConnections(prev => [...prev, newConnection]);
      setConnectDialogOpen(false);
    } catch (error) {
      console.error('Error connecting SNS:', error);
    }
  };

  const handleDisconnect = async (connectionId: string) => {
    try {
      await disconnectSNS(connectionId);
      setSnsConnections(prev => prev.filter(conn => conn.id !== connectionId));
    } catch (error) {
      console.error('Error disconnecting SNS:', error);
    }
  };

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: '/mypage' }} />;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const campaignStats = {
    신청: 1,
    선정: 0,
    등록: 0,
    종료: 0
  };

  return (
    <>
      <Header showIcons={true} />
      <Box sx={{ bgcolor: '#0066FF', color: 'white', py: 3, borderRadius: 2 }}>
        <Container maxWidth="md">
          <ProfileSection
            profile={profile}
          />
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Typography 
            component="h2" 
            sx={{ 
              fontSize: '1rem', 
              fontWeight: 500,
              mb: 2
            }}
          >
            나의 캠페인
          </Typography>
          <Paper 
            elevation={0} 
            sx={{ 
              bgcolor: '#F8F9FA',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: `repeat(${Object.keys(campaignStats).length}, 1fr)`,
              '& > *': {
                borderRight: '1px solid #eee',
                '&:last-child': {
                  borderRight: 'none'
                }
              }
            }}>
              {Object.entries(campaignStats).map(([status, count]) => (
                <Box 
                  key={status} 
                  sx={{ 
                    textAlign: 'center',
                    py: 2
                  }}
                >
                  <Typography 
                    color="text.secondary" 
                    variant="body2" 
                    sx={{ 
                      mb: 0.5, 
                      fontSize: '0.875rem',
                      color: 'rgba(0, 0, 0, 0.6)'
                    }}
                  >
                    {status}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 500,
                      color: '#0066FF',
                      fontSize: '1.25rem'
                    }}
                  >
                    {count}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>

        {/* Notifications */}
        <Box sx={{ mb: 4 }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2,
              bgcolor: '#F8F9FA',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <NotificationsOutlined sx={{ mr: 1, color: 'text.secondary', fontSize: '1.25rem' }} />
              <Typography sx={{ fontSize: '0.875rem' }}>
                알림 0개
              </Typography>
            </Box>
            <Link 
              href="/notifications"
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                color: '#0066FF',
                fontSize: '0.875rem',
                '&:hover': { 
                  textDecoration: 'none',
                  opacity: 0.8
                }
              }}
            >
              전체알림
              <ChevronRight sx={{ ml: 0.5, fontSize: '1rem' }} />
            </Link>
          </Paper>
        </Box>

        {/* SNS Connections */}
        <Box>
          <Typography 
            component="h2" 
            sx={{ 
              fontSize: '1rem', 
              fontWeight: 500,
              mb: 2
            }}
          >
            SNS 연결
          </Typography>
          <Paper 
            elevation={0}
            sx={{ 
              bgcolor: '#F8F9FA',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              '& > *:not(:last-child)': {
                borderBottom: '1px solid #eee'
              }
            }}>
              {SNS_TYPES.map(({ type, label, placeholder, icon: Icon }) => {
                const connection = snsConnections.find(conn => conn.type === type);
                
                return (
                  <Box 
                    key={type}
                    sx={{ 
                      p: 2, 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ 
                        mr: 1.5, 
                        color: connection ? '#0066FF' : '#9E9E9E',
                        display: 'flex', 
                        alignItems: 'center' 
                      }}>
                        <Icon size={20} />
                      </Box>
                      <Typography sx={{ 
                        fontSize: '0.875rem',
                        color: connection ? 'text.primary' : 'text.secondary'
                      }}>
                        {connection ? (connection.url || connection.username) : placeholder}
                      </Typography>
                    </Box>
                    <Button 
                      variant="outlined" 
                      size="small"
                      color={connection ? 'error' : 'primary'}
                      onClick={() => connection ? handleDisconnect(connection.id) : handleConnectClick(type)}
                      sx={{ 
                        minWidth: 60,
                        borderRadius: 1,
                        fontSize: '0.875rem'
                      }}
                    >
                      {connection ? '해제' : '연결'}
                    </Button>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Box>
      </Container>

      {/* SNS Connect Dialog */}
      <Dialog 
        open={connectDialogOpen} 
        onClose={() => setConnectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {SNS_TYPES.find(s => s.type === selectedSNSType)?.label} 연결
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={SNS_TYPES.find(s => s.type === selectedSNSType)?.placeholder}
            type="text"
            fullWidth
            variant="outlined"
            value={snsInput}
            onChange={(e) => setSnsInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnectDialogOpen(false)}>취소</Button>
          <Button onClick={handleConnect} variant="contained" disabled={!snsInput}>
            연결하기
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reviewer Drawer */}
      <ReviewerDrawer 
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </>
  );
};

export default ReviewerMyPage; 